/**
 * 터치 조작 — 좌측 가상 조이스틱(이동) + 우측 드래그(시야) + 액션 버튼.
 *
 * 설계 원칙:
 *  - 조이스틱은 **고정 위치가 아니라 손가락이 닿은 자리에 생긴다**. 작은 화면에서
 *    엄지 위치는 사람마다 다르고, 고정 패드는 화면을 보며 손가락을 찾게 만든다.
 *  - 이동과 시야는 반드시 **동시에** 되어야 한다 (pointerId 로 각각 추적).
 *  - 시야 드래그는 관성이 없다. 조사 게임에서 정확히 겨누는 게 더 중요하다.
 *  - 버튼은 pointerdown 에서 즉시 반응한다 (click 을 기다리면 300ms 가 체감된다).
 */

import type { LocalPlayer } from './player';

export interface TouchActions {
  interact: () => void;
  board: () => void;
  mark: () => void;
  chat: () => void;
  drop: () => void;
  toggleLight: () => void;
}

/** 손가락 하나가 화면에서 이 거리보다 적게 움직였고 짧게 뗐으면 '탭'으로 본다 */
const TAP_PX = 12;
const TAP_MS = 260;

export function isTouchDevice() {
  return (
    typeof matchMedia === 'function' &&
    (matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0)
  );
}

export class TouchControls {
  private root: HTMLElement;
  private stickBase: HTMLElement;
  private stickKnob: HTMLElement;

  /** 이동을 담당 중인 포인터 */
  private movePointer = -1;
  private moveOrigin = { x: 0, y: 0 };
  /** 시야를 담당 중인 포인터 */
  private lookPointer = -1;
  private lookLast = { x: 0, y: 0 };
  private lookStart = { x: 0, y: 0, t: 0 };

  /** 조이스틱 최대 반경(px) — 화면 크기에 맞춰 정한다 */
  private radius = 64;

  constructor(
    parent: HTMLElement,
    private player: LocalPlayer,
    private actions: TouchActions,
    private isModalOpen: () => boolean,
  ) {
    this.radius = Math.max(52, Math.min(84, Math.round(Math.min(innerWidth, innerHeight) * 0.13)));

    parent.insertAdjacentHTML(
      'beforeend',
      `<div id="touch">
         <div id="stick" hidden><div class="knob"></div></div>
         <div id="tbtns">
           <button class="tb tb-sm" data-a="light" aria-label="손전등">
             <svg viewBox="0 0 24 24"><path d="M9 2h6v4l-1.2 2.4V22h-3.6V8.4L9 6z"/></svg>
           </button>
           <button class="tb tb-sm" data-a="mark" aria-label="가리키기">
             <svg viewBox="0 0 24 24"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/></svg>
           </button>
           <button class="tb tb-sm" data-a="board" aria-label="증거 보드">
             <svg viewBox="0 0 24 24"><path d="M4 3h16v18H4zm3 4h10v2H7zm0 4h10v2H7zm0 4h7v2H7z"/></svg>
           </button>
           <button class="tb tb-sm" data-a="chat" aria-label="채팅">
             <svg viewBox="0 0 24 24"><path d="M3 4h18v12H8l-5 4z"/></svg>
           </button>
           <button class="tb tb-main" data-a="interact"><span>조사</span></button>
         </div>
         <button id="tdrop" class="tb tb-sm" data-a="drop" hidden aria-label="내려놓기">
           <svg viewBox="0 0 24 24"><path d="M12 3v12l4-4 1.4 1.4L12 18.8 6.6 12.4 8 11l4 4V3z"/><path d="M4 20h16v2H4z"/></svg>
         </button>
       </div>`,
    );

    this.root = document.getElementById('touch')!;
    this.stickBase = document.getElementById('stick')!;
    this.stickKnob = this.stickBase.querySelector('.knob')!;

    for (const b of this.root.querySelectorAll<HTMLElement>('[data-a]')) {
      // click 이 아니라 pointerdown — 모바일에서 click 은 눈에 띄게 늦다
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        b.classList.add('down');
        this.fire(b.dataset.a!);
      });
      const up = () => b.classList.remove('down');
      b.addEventListener('pointerup', up);
      b.addEventListener('pointercancel', up);
      b.addEventListener('pointerleave', up);
    }

    // 조작 영역은 캔버스 전체. 버튼 위에서 시작한 터치는 위에서 stopPropagation 으로 걸러진다.
    const surface = document.getElementById('view')!;
    surface.addEventListener('pointerdown', (e) => this.onDown(e as PointerEvent), { passive: false });
    surface.addEventListener('pointermove', (e) => this.onMove(e as PointerEvent), { passive: false });
    surface.addEventListener('pointerup', (e) => this.onUp(e as PointerEvent));
    surface.addEventListener('pointercancel', (e) => this.onUp(e as PointerEvent));

    // 두 손가락 확대/스크롤이 게임 조작을 방해하지 않게
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    document.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length > 1) e.preventDefault();
      },
      { passive: false },
    );
  }

  private fire(a: string) {
    switch (a) {
      case 'interact':
        this.actions.interact();
        break;
      case 'board':
        this.actions.board();
        break;
      case 'mark':
        this.actions.mark();
        break;
      case 'chat':
        this.actions.chat();
        break;
      case 'drop':
        this.actions.drop();
        break;
      case 'light':
        this.actions.toggleLight();
        this.root.querySelector('[data-a="light"]')!.classList.toggle('on', this.player.lightIsOn);
        break;
    }
  }

  private onDown(e: PointerEvent) {
    if (this.isModalOpen()) return;
    e.preventDefault();
    const leftHalf = e.clientX < innerWidth * 0.45;

    if (leftHalf && this.movePointer === -1) {
      this.movePointer = e.pointerId;
      this.moveOrigin = { x: e.clientX, y: e.clientY };
      this.showStick(e.clientX, e.clientY);
      return;
    }
    if (this.lookPointer === -1) {
      this.lookPointer = e.pointerId;
      this.lookLast = { x: e.clientX, y: e.clientY };
      this.lookStart = { x: e.clientX, y: e.clientY, t: performance.now() };
    }
  }

  private onMove(e: PointerEvent) {
    if (e.pointerId === this.movePointer) {
      e.preventDefault();
      const dx = e.clientX - this.moveOrigin.x;
      const dy = e.clientY - this.moveOrigin.y;
      const dist = Math.hypot(dx, dy);
      const clamped = Math.min(dist, this.radius);
      const nx = dist > 0 ? (dx / dist) * (clamped / this.radius) : 0;
      const ny = dist > 0 ? (dy / dist) * (clamped / this.radius) : 0;
      // 화면 위쪽으로 밀면 전진
      this.player.moveInput.x = nx;
      this.player.moveInput.y = -ny;
      this.stickKnob.style.transform = `translate(${nx * this.radius}px, ${ny * this.radius}px)`;
      return;
    }

    if (e.pointerId === this.lookPointer) {
      e.preventDefault();
      // 화면 크기에 비례한 감도 — 큰 화면에서 같은 손가락 이동이 더 크게 돌면 어지럽다
      const s = 2.6 / Math.min(innerWidth, innerHeight);
      this.player.applyLook((e.clientX - this.lookLast.x) * s, (e.clientY - this.lookLast.y) * s);
      this.lookLast = { x: e.clientX, y: e.clientY };
    }
  }

  private onUp(e: PointerEvent) {
    if (e.pointerId === this.movePointer) {
      this.movePointer = -1;
      this.player.moveInput.x = 0;
      this.player.moveInput.y = 0;
      this.hideStick();
      return;
    }
    if (e.pointerId === this.lookPointer) {
      this.lookPointer = -1;
      // 거의 움직이지 않고 짧게 뗐다면 조사 = 화면을 그냥 톡 치면 상호작용
      const moved = Math.hypot(e.clientX - this.lookStart.x, e.clientY - this.lookStart.y);
      const held = performance.now() - this.lookStart.t;
      if (moved < TAP_PX && held < TAP_MS && !this.isModalOpen()) this.actions.interact();
    }
  }

  private showStick(x: number, y: number) {
    this.stickBase.hidden = false;
    this.stickBase.style.width = this.stickBase.style.height = `${this.radius * 2}px`;
    this.stickBase.style.left = `${x - this.radius}px`;
    this.stickBase.style.top = `${y - this.radius}px`;
    this.stickKnob.style.transform = 'translate(0,0)';
  }

  private hideStick() {
    this.stickBase.hidden = true;
  }

  /** 소지품이 있을 때만 내려놓기 버튼을 띄운다 */
  setHolding(holding: boolean) {
    (document.getElementById('tdrop') as HTMLElement).hidden = !holding;
  }

  /** 조준 대상이 있을 때 조사 버튼을 강조 */
  setAimed(label: string | null) {
    const b = this.root.querySelector<HTMLElement>('[data-a="interact"]')!;
    b.classList.toggle('hot', !!label);
    b.querySelector('span')!.textContent = label ?? '조사';
  }
}
